import { Component, EventEmitter, OnInit, Output, Input, HostListener, ElementRef } from '@angular/core';
import { Household, GetHouseholdsResponse } from 'src/app/modules/shared/models/households.model';
import { HouseholdsService } from 'src/app/modules/shared/services/households/households.service';
import { AdvisorHouseholdsService } from 'src/app/modules/shared/services/advisor-households/advisor-households.service';
import { UserRoleService, UserRole } from 'src/app/modules/shared/services/user-role/user-role.service';
import { ToasterService } from 'src/app/modules/shared/services/toaster/toaster.service';

@Component({
  selector: 'app-household-filter',
  templateUrl: './household-filter.component.html',
  styleUrls: ['./household-filter.component.scss']
})
export class HouseholdFilterComponent implements OnInit {
  /** The list of all households (fetched from API based on search) */
  households: Household[] = [];

  /** Full unfiltered household list cached after first API load (enables client-side case-insensitive search) */
  private households_cache: Household[] = [];

  /** Dictionary/Map for quick household lookup by ID */
  household_lookup: Map<number, Household> = new Map();

  /** Loading state for household fetch */
  is_loading_households = false;

  /** Dropdown open state */
  is_dropdown_open = false;

  /** Search query for filtering households */
  search_query: string = '';

  /** Debounce timer for search */
  private search_timeout: ReturnType<typeof setTimeout> | null = null;

  /** Currently selected household ID (null = "All Households") */
  @Input() selected_household_id: number | null = null;

  /** When true, renders in compact inline mode (no label, smaller padding) for use in control rows */
  @Input() compact = false;

  /** Emits when a household filter is selected */
  @Output() household_filter_changed = new EventEmitter<number | null>();

  /** Emits the household lookup map after loading (for parent component to use) */
  @Output() household_lookup_loaded = new EventEmitter<Map<number, Household>>();

  constructor(
    private households_service: HouseholdsService,
    private advisor_households_service: AdvisorHouseholdsService,
    private user_role_service: UserRoleService,
    private toaster: ToasterService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    void this.loadHouseholds();
  }

  /**
   * Load households from API with optional search query.
   * ADVISOR role: fetches only assigned households via AdvisorHouseholdsService.
   * All other roles: fetches full org-scoped list via HouseholdsService.
   *
   * Both paths filter client-side (case-insensitive) from a cached full list so
   * that searching "smith" also matches "Smith" or "SMITH".
   * Client never injects a scope override — server enforces access boundaries.
   */
  public async loadHouseholds(searchQuery: string = ''): Promise<void> {
    this.is_loading_households = true;

    try {
      const isAdvisor = this.user_role_service.getCurrentRole() === UserRole.ADVISOR;

      if (isAdvisor) {
        // Advisor: load only assigned households; server already scopes by advisor.
        // Populate cache on first load (empty search), then filter client-side.
        if (!searchQuery || this.households_cache.length === 0) {
          const response = await this.advisor_households_service.getMyAssignedHouseholds(100, 0);
          const assignments = response.data?.assignments ?? [];

          this.households_cache = assignments.map(a => ({
            HOUSEHOLD_ID: a.household.HOUSEHOLD_ID,
            HOUSEHOLD_NAME: a.household.HOUSEHOLD_NAME ?? '',
            ORGANIZATION_ID: a.household.ORGANIZATION_ID,
          } as Household));
        }
      } else {
        // Manager / Supervisor / SuperAdmin: populate cache on first load (empty search).
        // Subsequent searches filter the cache client-side so matching is case-insensitive.
        if (!searchQuery || this.households_cache.length === 0) {
          const response: GetHouseholdsResponse = await this.households_service.listHouseholds({
            page: 1,
            pageSize: 500,
          });
          this.households_cache = response.households || [];
        }
      }

      // Apply case-insensitive client-side filter from the cache
      const q = searchQuery.toLowerCase();
      this.households = searchQuery
        ? this.households_cache.filter(h => h.HOUSEHOLD_NAME.toLowerCase().includes(q))
        : this.households_cache;

      // Build lookup map for O(1) access - update with all households we've seen
      this.households_cache.forEach(household => {
        this.household_lookup.set(household.HOUSEHOLD_ID, household);
      });

      // Emit the lookup map to parent component (on first load or when we add new households)
      this.household_lookup_loaded.emit(this.household_lookup);

      console.log('[HouseholdFilterComponent] Loaded households:', this.households.length, '| role:', this.user_role_service.getCurrentRole());
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Error loading households: ${msg}`, 4000);
      console.error('[HouseholdFilterComponent] Error loading households:', error);
    } finally {
      this.is_loading_households = false;
    }
  }

  /**
   * Handle search input changes with debouncing
   */
  public onSearchChange(query: string): void {
    this.search_query = query;

    // Clear existing timeout
    if (this.search_timeout) {
      clearTimeout(this.search_timeout);
    }

    // Debounce search by 300ms
    this.search_timeout = setTimeout(() => {
      void this.loadHouseholds(query);
    }, 300);
  }

  /**
   * Toggle dropdown open/close
   */
  public toggleDropdown(): void {
    this.is_dropdown_open = !this.is_dropdown_open;

    if (this.is_dropdown_open) {
      // Reset search and cache when opening so stale data is refreshed
      this.search_query = '';
      this.households_cache = [];
      void this.loadHouseholds();
    }
  }

  /**
   * Handler for household selection
   */
  public selectHousehold(householdId: number | null): void {
    this.selected_household_id = householdId;
    this.is_dropdown_open = false;
    this.search_query = '';
    this.household_filter_changed.emit(householdId);
  }

  /**
   * Get the display name for the currently selected household
   */
  public getSelectedHouseholdDisplayName(): string {
    if (!this.selected_household_id) {
      return 'All Households';
    }
    const household = this.household_lookup.get(this.selected_household_id);
    return household ? household.HOUSEHOLD_NAME : 'Unknown Household';
  }

  /**
   * Close dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.is_dropdown_open = false;
    }
  }

  /**
   * Prevent dropdown from closing when clicking inside search input
   */
  public onSearchInputClick(event: Event): void {
    event.stopPropagation();
  }
}
